import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountDropdown from '../components/AccountDropdown';
import logo from '../assets/logo128x128.png';
import MapCard from '../components/MapCard';
import '../Homepage.css';

function capitalise(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDisplayName(map) {
  if (
    !map.name ||
    map.name.trim() === '' ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

function FavouritesPage() {
  const [maps, setMaps] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [votesData, setVotesData] = useState({});
  const [votesLoading, setVotesLoading] = useState({});
  const [favLoading, setFavLoading] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUser(data);
        if (data) {
          return fetch('/api/user/favourites', { credentials: 'include' });
        }
      })
      .then(res => {
        if (res) return res.json();
      })
      .then(favouritesData => setMaps(favouritesData || []));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!maps.length) return;
    const loadVotes = async () => {
      const newVotesData = {};
      for (let map of maps) {
        const res = await fetch(`/api/map-votes/${map.id}/${user?.id || 0}`);
        const data = await res.json();
        newVotesData[map.id] = data;
      }
      setVotesData(newVotesData);
    };
    loadVotes();
  }, [maps, user]);

  const handleVote = async (mapId, vote) => {
    if (!user) return;
    setVotesLoading(v => ({ ...v, [mapId]: true }));
    await fetch(`/api/map-votes/${mapId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, vote }),
    });
    const res = await fetch(`/api/map-votes/${mapId}/${user.id}`);
    const data = await res.json();
    setVotesData(v => ({ ...v, [mapId]: data }));
    setVotesLoading(v => ({ ...v, [mapId]: false }));
  };

  const handleFavourite = async (mapId, isFavourited) => {
    if (!user) return;
    setFavLoading(l => ({ ...l, [mapId]: true }));
    await fetch(`/api/user/favourites/${mapId}`, {
      method: isFavourited ? 'DELETE' : 'POST',
      headers: { "Content-Type": "application/json" },
      credentials: 'include'
    });
    fetch('/api/user/favourites', { credentials: 'include' })
      .then(res => res.json())
      .then(data => setMaps(data));
    setFavLoading(l => ({ ...l, [mapId]: false }));
  };

  const filteredMaps = maps.filter(map =>
    (map.name && map.name.toLowerCase().includes(search.toLowerCase())) ||
    (map.game_name && map.game_name.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div style={{ padding: 20, color: 'white' }}>Loading...</div>;
  if (!user) return <div style={{ padding: 20, color: 'white' }}>Please log in to see your favourites.</div>;

  return (
    <div className="homepage">
      <header className="homepage-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <img
          src={logo}
          alt="Point Atlas Logo"
          style={{ width: 40, height: 40, objectFit: 'contain', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Go to Home"
        />
        <h1 className="brand" style={{ margin: 0 }}>Point Atlas - Favourites</h1>
        <input
          className="search-bar"
          type="text"
          placeholder="Search maps..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <AccountDropdown user={user} />
      </header>

      <div className="maps-grid">
        {filteredMaps.length === 0 && (
          <p style={{ color: 'white' }}>
            You haven't favourited any maps yet.
          </p>
        )}
        {filteredMaps.map((map) => (
          <MapCard
            key={map.id}
            map={map}
            user={user}
            showVotes={true}
            votes={votesData[map.id]?.total_votes}
            userVote={votesData[map.id]?.user_vote}
            onVote={(id, vote) => handleVote(id, vote)}
            isFavourited={true}
            favLoading={favLoading[map.id]}
            onFavourite={() => handleFavourite(map.id, true)}
            showFavouriteButton={true}
          />
        ))}
      </div>
    </div>
  );
}

export default FavouritesPage;
